"use client";

import { useCallback, useRef, useState } from "react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { AssetImage } from "@/components/optilase/asset-image";
import { CarouselDots } from "@/components/optilase/carousel-dots";
import { activeSlide } from "@/components/optilase/carousel-page";
import { CtaButton } from "@/components/optilase/cta-button";
import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

type Asset = { filename?: string; alt?: string; focus?: string };

type LpStep = SbBlokData & { title?: string; body?: string; image?: Asset };

type LpStepsBlok = SbBlokData & {
  heading?: string;
  intro?: string;
  steps?: LpStep[];
  tint?: boolean;
  cta_label?: string;
  bg_color?: unknown;
};

function layout(count: number) {
  if (count >= 4) {
    return {
      columns: "md:grid-cols-2 lg:grid-cols-4",
      circle: "max-w-75 xl:max-w-56",
      dashY: "mt-[min(50%,112px)]",
    };
  }
  return {
    columns: count === 2 ? "md:grid-cols-2 lg:grid-cols-2" : "md:grid-cols-3 lg:grid-cols-3",
    circle: "max-w-75",
    dashY: "mt-[min(50%,150px)]",
  };
}

export default function LpSteps({ blok }: { blok: LpStepsBlok }) {
  const scroller = useRef<HTMLOListElement>(null);
  const [page, setPage] = useState(0);
  const sync = useCallback(() => {
    const el = scroller.current;
    if (el) setPage(activeSlide(el));
  }, []);

  const steps = blok.steps ?? [];
  const surface = surfaceFrom(blok.bg_color);
  const grid = layout(steps.length);
  if (steps.length === 0) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn(
        "px-5 py-12 md:px-10 lg:px-20 lg:py-20",
        surface ? "" : blok.tint ? "bg-teal-01" : "bg-white",
      )}
    >
      <div className="mx-auto w-full max-w-7xl">
        {blok.heading && (
          <h2
            className={cn(
              "text-section mx-auto max-w-230 text-center text-balance",
              headingOn(surface, blok.tint ? "text-teal-08" : "text-teal-07"),
            )}
          >
            {blok.heading}
          </h2>
        )}
        {blok.intro && (
          <p
            className={cn(
              "mx-auto mt-4 max-w-230 text-center text-base leading-relaxed lg:text-xl",
              onSurface(surface, "text-white/80", "text-grey-07", "text-grey-07"),
            )}
          >
            {blok.intro}
          </p>
        )}

        <ol
          ref={scroller}
          onScroll={sync}
          className={cn(
            "mt-8 flex snap-x snap-mandatory scrollbar-none gap-8 overflow-x-auto pt-7 pb-2",
            "md:mt-14 md:grid md:snap-none md:gap-x-6 md:gap-y-16 md:overflow-visible md:pt-9 md:pb-0",
            "lg:gap-x-8",
            grid.columns,
          )}
        >
          {steps.map((step, i) => (
            <li
              key={step._uid as string}
              {...storyblokEditable(step)}
              className="relative flex w-full shrink-0 snap-start flex-col items-center gap-6 text-center md:w-auto md:shrink"
            >
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    "border-teal-03 pointer-events-none absolute top-0 left-full hidden w-10",
                    "translate-x-[calc(1rem-50%)] -translate-y-1/2 border-t-2 border-dashed xl:block",
                    grid.dashY,
                  )}
                />
              )}

              {step.image?.filename ? (
                <div className={cn("relative mx-auto aspect-square w-full", grid.circle)}>
                  <div className="relative size-full overflow-hidden rounded-full">
                    <AssetImage
                      src={step.image.filename}
                      focus={step.image.focus}
                      alt={step.image.alt || ""}
                      fill
                      sizes="(min-width: 1024px) 300px, (min-width: 768px) 30vw, 250px"
                      className="object-cover"
                    />
                  </div>
                  <span
                    aria-hidden
                    className="bg-teal-08 absolute top-0 left-1/2 flex size-15 -translate-x-1/2 translate-y-[-45%] items-center justify-center rounded-full text-[28px] leading-none font-semibold tracking-[0.02em] text-white lg:size-18"
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              ) : (
                <span
                  aria-hidden
                  className="bg-teal-08 flex size-15 items-center justify-center rounded-full text-[28px] leading-none font-semibold tracking-[0.02em] text-white lg:size-18"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
              )}

              <div className="flex w-full max-w-94 flex-col gap-2">
                {step.title && (
                  <p
                    className={cn(
                      "text-2xl leading-[1.2] font-semibold tracking-[-0.01em]",
                      onSurface(surface, "text-white", "text-grey-09", "text-grey-09"),
                    )}
                  >
                    {step.title}
                  </p>
                )}
                {step.body && (
                  <p
                    className={cn(
                      "text-body",
                      onSurface(surface, "text-white/80", "text-grey-07", "text-grey-07"),
                    )}
                  >
                    {step.body}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>

        <CarouselDots
          count={steps.length}
          active={page}
          label="step"
          onSelect={(i) => {
            const item = scroller.current?.children[i] as HTMLElement | undefined;
            item?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
          }}
          className="md:hidden"
        />

        {blok.cta_label && (
          <div className="mt-10 flex justify-center">
            <CtaButton
              action="open_booking"
              variant="orange"
              keepOnMobile
              className="w-full sm:w-fit"
            >
              {blok.cta_label}
            </CtaButton>
          </div>
        )}
      </div>
    </section>
  );
}
